import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

const Methodology = () => {
  const { t, i18n } = useTranslation();
  const [isVisible, setIsVisible] = useState(false);
  const [activeStep, setActiveStep] = useState(0);
  const sectionRef = useRef(null);
  const isRTL = i18n.language === 'ar';
  const steps = t('methodology.steps', { returnObjects: true });

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <section
      id="process"
      ref={sectionRef}
      className="relative py-24 md:py-32 bg-black"
      dir={isRTL ? 'rtl' : 'ltr'}
    >
      {/* Grid Background */}
      <div className="absolute inset-0 grid-pattern opacity-30" />

      <div className="relative z-10 max-w-[1400px] mx-auto px-5 md:px-[7.6923%]">
        {/* Section Header */}
        <div className="text-center mb-20">
          <div
            className={`inline-flex items-center gap-3 mb-6 opacity-0 ${
              isVisible ? 'animate-fade-in-up' : ''
            }`}
          >
            <span className="w-12 h-[2px] bg-[#00FFD1]" />
            <span className="text-[#00FFD1] text-sm font-medium tracking-wider uppercase">
              {t('methodology.title')}
            </span>
            <span className="w-12 h-[2px] bg-[#00FFD1]" />
          </div>

          <h2
            className={`display-large text-white mb-4 opacity-0 ${
              isVisible ? 'animate-fade-in-up delay-100' : ''
            }`}
          >
            {t('methodology.subtitle')}
          </h2>
        </div>

        {/* Steps Timeline - Desktop */}
        <div className="hidden lg:block relative">
          {/* Progress Line */}
          <div className="absolute top-12 left-0 w-full h-[2px] bg-white/10">
            <div
              className="h-full bg-[#00FFD1] transition-all duration-700"
              style={{ width: `${((activeStep + 1) / steps.length) * 100}%` }}
            />
          </div>

          {/* Steps */}
          <div className="grid grid-cols-4 gap-8">
            {steps.map((step, index) => (
              <div
                key={step.number}
                className={`relative pt-20 cursor-pointer opacity-0 ${
                  isVisible ? `animate-fade-in-up delay-${(index + 2) * 100}` : ''
                }`}
                onMouseEnter={() => setActiveStep(index)}
              >
                {/* Step Marker */}
                <div
                  className={`absolute top-6 left-1/2 -translate-x-1/2 w-12 h-12 flex items-center justify-center border-2 transition-all duration-500 ${
                    index <= activeStep
                      ? 'bg-[#00FFD1] border-[#00FFD1] text-black'
                      : 'bg-black border-white/20 text-white/40'
                  }`}
                >
                  <span className="font-semibold text-sm">{step.number}</span>
                </div>

                {/* Step Content */}
                <div
                  className={`text-center transition-all duration-500 ${
                    index === activeStep ? 'opacity-100' : 'opacity-60'
                  }`}
                >
                  <h3
                    className={`text-lg font-semibold mb-3 transition-colors duration-300 ${
                      index === activeStep ? 'text-[#00FFD1]' : 'text-white'
                    }`}
                  >
                    {step.title}
                  </h3>
                  <p className="text-white/60 text-sm leading-relaxed">
                    {step.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Steps List - Mobile/Tablet */}
        <div className="lg:hidden space-y-6">
          {steps.map((step, index) => (
            <div
              key={step.number}
              className={`relative flex gap-6 p-6 bg-white/[0.02] border border-white/10 opacity-0 ${
                isVisible ? `animate-fade-in-up delay-${(index + 2) * 100}` : ''
              }`}
            >
              {/* Step Number */}
              <div className="flex-shrink-0 w-16 h-16 flex items-center justify-center bg-[#00FFD1]/10 border border-[#00FFD1]/30">
                <span className="text-2xl font-bold text-[#00FFD1]">{step.number}</span>
              </div>

              {/* Content */}
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-white mb-2">
                  {step.title}
                </h3>
                <p className="text-white/60 text-sm leading-relaxed">
                  {step.description}
                </p>
              </div>

              {/* Connector Line */}
              {index < steps.length - 1 && (
                <div className={`absolute bottom-0 ${isRTL ? 'right-14' : 'left-14'} w-[2px] h-6 bg-[#00FFD1]/30 translate-y-full`} />
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Methodology;
